import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import LoadingSpinner from "../../component/LoadingSpinner";
import { serviceFleetGetFleetLogs } from "../../api/serviceFleetMaster";

const ITEMS_PER_PAGE = 50;

function nowForInput() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${hh}:${mm}`;
}

function toUtcISOString(datetimeLocalValue) {
  const dt = new Date(datetimeLocalValue); // local
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

function formatLocal(utcTimestamp) {
  // Works whether timestamp is ISO string or epoch millis (code sorts numeric; table displays item.timestamp)
  const dt = new Date(utcTimestamp);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}
// function localDisplay(utcTimestamp) {
//   return moment.utc(utcTimestamp).local().format('MMM D, YYYY, h:mm:ss A');
// }

function escapeCsvCell(value) {
  const s = value == null ? "" : String(value);
  // Quote + escape quotes
  return `"${s.replace(/"/g, '""')}"`;
}

export default function FleetRegoTable({
  vehicle,
  controlPanelOpen,
  setControlPanelOpen,
}) {
  const [fleetData, setFleetData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [rowFilter, setRowFilter] = useState("");
  const [page, setPage] = useState(1);

  // default last 7 days
  useEffect(() => {
    const now = new Date();
    const seven = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    setStartDate(seven.toISOString().slice(0, 16));
    setEndDate(nowForInput());
  }, []);

  const serverGetFleetLogs = useCallback(
    async ({ vehicleId, startLocal, endLocal }) => {
      const startIso = toUtcISOString(startLocal);
      const endIso = toUtcISOString(endLocal);

      if (!startIso || !endIso) {
        toast.error("Please enter valid start & end dates.");
        return false;
      }

      if (new Date(startIso) > new Date(endIso)) {
        toast.error("Start date must be older than end date.");
        return false;
      }

      try {
        setLoading(true);
        setPage(1);

        const data = await serviceFleetGetFleetLogs({
          vehicleId,
          startIso,
          endIso,
        });

        setFleetData(data);

        return data.length > 0;
      } catch (err) {
        setFleetData([]);
        toast.error(err.message || "Failed to fetch fleet data");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // auto fetch when vehicle changes AND dates are ready
  useEffect(() => {
    if (!vehicle) return;
    if (!startDate || !endDate) return; // important: wait until defaults are set

    serverGetFleetLogs({
      vehicleId: vehicle._id,
      startLocal: startDate,
      endLocal: endDate,
    });
  }, [vehicle, startDate, endDate, serverGetFleetLogs]);

  const handleSearch = async () => {
    if (!vehicle) {
      toast.error("Select a vehicle first.");
      return;
    }

    const hasData = await serverGetFleetLogs({
      vehicleId: vehicle._id,
      startLocal: startDate,
      endLocal: endDate,
    });

    if (hasData) {
      setControlPanelOpen(false);
    }
  };

  const filtered = useMemo(() => {
    const q = rowFilter.trim().toLowerCase();
    if (!q) return fleetData;
    return fleetData.filter((item) =>
      Object.values(item).join(" ").toLowerCase().includes(q),
    );
  }, [fleetData, rowFilter]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages);

  const paginated = useMemo(() => {
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, safePage]);

  const download = (format) => {
    try {
      if (!filtered.length) return;

      if (format === "json") {
        const blob = new Blob([JSON.stringify(filtered, null, 2)], {
          type: "application/json",
        });
        saveAs(blob, "fleetmaster_fleetdata.json");
        return;
      }

      if (format === "csv") {
        const headers = Object.keys(filtered[0] ?? {});
        const lines = [
          headers.map(escapeCsvCell).join(","),
          ...filtered.map((row) =>
            headers.map((h) => escapeCsvCell(row?.[h])).join(","),
          ),
        ];

        // Add UTF-8 BOM for Excel compatibility
        const csv = "\uFEFF" + lines.join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        saveAs(blob, "fleetmaster_fleetdata.csv");
      }
    } catch {
      toast.error("Failed to download data.");
    }
  };

  //address lat,lng show and click
  const latLngText = (item) => {
    const [lon, lat] = item.location?.coordinates || [];
    if (lat == null || lon == null) return "";
    return `${lat.toFixed(6)}, ${lon.toFixed(6)}`;
  };

  const openInMaps = (item) => {
    const [lon, lat] = item.location?.coordinates || [];
    if (lat == null || lon == null) return;

    const url = `https://www.google.com/maps?q=${lat},${lon}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderPager = () => {
    const pages = [];
    const max = 5;
    const start = Math.max(1, safePage - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);

    if (start > 1) pages.push(1, "…");
    for (let p = start; p <= end; p++) pages.push(p);
    if (end < totalPages) pages.push("…", totalPages);

    return (
      <div
        className="fleettable-pager"
        role="navigation"
        aria-label="Pagination"
      >
        <button
          className="fleettable-btn ghost"
          onClick={() => setPage(1)}
          disabled={safePage === 1}
        >
          <i className="bi bi-chevron-double-left" aria-hidden="true" />
          First
        </button>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage((v) => Math.max(1, v - 1))}
          disabled={safePage === 1}
        >
          <i className="bi bi-chevron-left" aria-hidden="true" />
          Prev
        </button>

        <div className="fleettable-pager-pages" aria-label="Page buttons">
          {pages.map((p, idx) =>
            p === "…" ? (
              <span key={`e-${idx}`} className="fleettable-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`fleettable-page-btn ${p === safePage ? "is-active" : ""}`}
                onClick={() => setPage(p)}
                disabled={p === safePage}
                aria-current={p === safePage ? "page" : undefined}
              >
                {p}
              </button>
            ),
          )}
        </div>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage((v) => Math.min(totalPages, v + 1))}
          disabled={safePage === totalPages}
        >
          Next
          <i className="bi bi-chevron-right" aria-hidden="true" />
        </button>

        <button
          className="fleettable-btn ghost"
          onClick={() => setPage(totalPages)}
          disabled={safePage === totalPages}
        >
          Last
          <i className="bi bi-chevron-double-right" aria-hidden="true" />
        </button>
      </div>
    );
  };

  if (!vehicle) {
    return (
      <div className="fleettable-empty-center">
        <div className="fleettable-empty-card fleettable-pop">
          <div className="fleettable-empty-icon">
            <i className="bi bi-truck-front" aria-hidden="true" />
          </div>
          <div className="fleettable-empty-title">Select a vehicle</div>
          <div className="fleettable-empty-sub">
            Select a vehicle, adjust data range and search to load HaulWeigh
            logged fleetdata.
          </div>
          <div className="fleettable-empty-hint">
            <i className="bi bi-lightning-charge" aria-hidden="true" />
            Tip: select a vehicle to load the last 7 days
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fleettable-table-wrap">
      <div
        className={`fleettable-controls-accordion ${controlPanelOpen ? "is-open" : "is-closed"}`}
      >
        <div className="fleettable-controls-accordion-inner">
          <div className="fleettable-controls">
            <div className="fleettable-controls-holder">
              <div className="fleettable-field">
                <label>Start</label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  max={nowForInput()}
                />
              </div>

              <div className="fleettable-field">
                <label>End</label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  max={nowForInput()}
                />
              </div>

              <div className="fleettable-field grow">
                <label>Filter rows</label>
                <input
                  type="text"
                  placeholder="Type to filter…"
                  value={rowFilter}
                  onChange={(e) => setRowFilter(e.target.value)}
                />
              </div>
            </div>

            <div className="fleettable-controls-holder">
              <div className="fleettable-field">
                <label>Export</label>
                <select
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v) download(v);
                    e.target.value = "";
                  }}
                  defaultValue=""
                  disabled={!filtered.length}
                >
                  <option value="">Download…</option>
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <button
                className="fleettable-btn primary"
                onClick={handleSearch}
                disabled={loading}
              >
                <i className="bi bi-search" aria-hidden="true" /> Search
              </button>
            </div>

            <button
              type="button"
              className="fleettable-btn-float"
              onClick={() => setControlPanelOpen(false)}
              title="Hide controls"
            >
              <i className="bi bi-eye-slash" aria-hidden="true" />
              Hide
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="fleettable-loading">
          <LoadingSpinner loadingText={"Loading records…"} />
        </div>
      ) : paginated.length === 0 ? (
        <div className="fleettable-no-data fleettable-fade-up">
          <i className="bi bi-inbox" aria-hidden="true" />
          <span>No data available for this range.</span>
        </div>
      ) : (
        <>
          <div className="fleettable-table-scroller">
            <table className="fleettable-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th className="wide">Address</th>
                  {/* <th>Latitude</th> */}
                  {/* <th>Longitude</th> */}
                  <th>Total</th>
                  <th>Steer</th>
                  <th>Drive</th>
                  <th>Trailer A</th>
                  <th>Trailer B</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((item, idx) => (
                  <tr key={`${item?.id ?? "x"}-${item?.timestamp ?? idx}`}>
                    <td className="mono">{formatLocal(item.timestamp)}</td>
                    <td
                      className="wide address-cell clickable"
                      title={latLngText(item)}
                      onClick={() => openInMaps(item)}
                    >
                      {item.address || "-"}
                    </td>
                    {/* <td>{item.latitude}</td> */}
                    {/* <td>{item.longitude}</td> */}
                    <td className="strong">
                      {(item.weights?.steer || 0) +
                        (item.weights?.drive || 0) +
                        (item.weights?.trailerA || 0) +
                        (item.weights?.trailerB || 0)}
                    </td>
                    <td>{item.weights?.steer}</td>
                    <td>{item.weights?.drive}</td>
                    <td>{item.weights?.trailerA}</td>
                    <td>{item.weights?.trailerB}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="fleettable-footer">
            <div className="fleettable-count">
              <i className="bi bi-list-check" aria-hidden="true" /> {totalItems}{" "}
              items
            </div>
            {renderPager()}
          </div>
        </>
      )}
    </div>
  );
}
